import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { QcChartPoint, QcChartSummary } from '../../services/qcService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title);

/**
 * Blank chart — values must stay below the LOQ (limit of quantification).
 * Uses the chart summary's UCL as the LOQ when available; otherwise falls back
 * to a reasonable default. Out-of-control points are highlighted in red.
 */

export interface BlankChartProps {
  summary: QcChartSummary | null;
  points: QcChartPoint[];
  height?: number;
  loq?: number;
  yLabel?: string;
}

export const BlankChart: React.FC<BlankChartProps> = ({
  summary,
  points,
  height = 280,
  loq,
  yLabel,
}) => {
  const data = useMemo(() => {
    const labels = points.map((p) =>
      new Date(p.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    );
    const values = points.map((p) => Number(p.value));
    const limit = loq ?? summary?.ucl ?? 0.01;

    const colors = points.map((p) =>
      p.out_of_control ? '#d32f2f' : p.warning_level ? '#ef6c00' : '#2e7d32',
    );

    return {
      labels,
      datasets: [
        {
          label: 'Blanc — valeur mesurée',
          data: values,
          borderColor: '#0288d1',
          backgroundColor: 'rgba(2, 136, 209, 0.12)',
          pointBackgroundColor: colors,
          pointBorderColor: colors,
          tension: 0.15,
          pointRadius: 4,
        },
        {
          label: `LOQ (${limit})`,
          data: labels.map(() => limit),
          borderColor: '#d32f2f',
          borderDash: [4, 4],
          pointRadius: 0,
          fill: false,
        },
      ],
    };
  }, [points, summary, loq]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' as const, labels: { boxWidth: 12 } },
        tooltip: { mode: 'index' as const, intersect: false },
      },
      scales: {
        y: { title: { display: true, text: yLabel || 'Valeur (% / ppm / g/t)' }, beginAtZero: true },
        x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
      },
    }),
    [yLabel],
  );

  return (
    <div style={{ height }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default BlankChart;
